// components/layout/NavMenu/hooks/useNavBadgeCounts.ts
/**
 * Permission-gated nav badge counts. Split out from
 * useNavSubscriptions so the always-on listeners (tournaments,
 * onBreak, acting-lead) can run BEFORE permissions are known —
 * which lets NavMenu call useNavSubscriptions exactly once.
 *
 * Owns:
 *   - pendingAppeals: 30s polling against /tournaments/{id}/appeals
 *     for tournaments the appeals .read rule will let this user
 *     through on. Pre-filtered to match the rule so we never fire
 *     a denied read.
 *   - pendingUsers: live query for unapproved users (gated on
 *     canManage).
 *   - deckCheckTournaments: derived from tournaments + permissions
 *     to drive the Deck Check nav item's visibility.
 *
 * Penalties are intentionally returned as 0 — the count would
 * require downloading the full player subtree (~5MB) per
 * tournament. Surfaced on the Penalty Approval page directly.
 */
import { useState, useEffect, useMemo } from 'react';
import { ref, onValue, query, orderByChild, equalTo } from 'firebase/database';
import { db } from '../../../../config/firebase';
import type { UserData } from '../../../../context/AuthContext';
import type { NavPermissions } from './useNavPermissions';
import type { NavTournament } from './useNavSubscriptions';

interface Appeal {
  status?: string;
}

export interface NavBadgeCountsInput {
  user: UserData | null;
  permissions: NavPermissions | null;
  tournaments: NavTournament[];
}

export interface NavBadgeCountsResult {
  pendingAppeals: number;
  /** Always 0 — see file header for the reasoning. */
  pendingPenalties: number;
  pendingUsers: number;
  deckCheckTournaments: NavTournament[];
}

export function useNavBadgeCounts({
  user,
  permissions,
  tournaments,
}: NavBadgeCountsInput): NavBadgeCountsResult {
  const [pendingAppeals, setPendingAppeals] = useState(0);
  const [pendingUsers, setPendingUsers] = useState(0);

  const role = user?.role || '';
  const accountType = user?.accountType;

  // NAV BADGE APPEALS: live subscription, pre-filtered to the rule.
  //
  // Mounts one onValue() per *readable* tournament so the badge updates in
  // real time as appeals are raised/concluded — no reload, no 30s lag.
  //
  // The two historical cost concerns are addressed by the pre-filter:
  //   1) Permission denies — we filter to exactly the set the appeals .read
  //      rule allows BEFORE subscribing, so no denied reads are fired.
  //   2) Bandwidth — appeals subtrees are small (a handful of entries), so
  //      streaming them on change is negligible; we only subscribe to
  //      tournaments this user can actually see.
  // Stable keys for the effect deps: the effect must NOT depend on the whole
  // `user` object — AuthContext re-emits it on ANY write to the user record
  // (break toggles, lastSeenWhatsNew stamps, team assignments…), and each
  // re-run tears down and re-attaches every appeals listener, re-downloading
  // every appeals node. On an event day that was a constant re-subscribe
  // storm across all staff.
  const tournamentIdsKey = tournaments.map((t) => t.id).join(',');
  const assignedKey = Object.keys(user?.tournamentAssignments || {}).sort().join(',');
  const hjTeamKey = Object.keys(user?.headJudgeTeamAssignments || {}).sort().join(',');

  useEffect(() => {
    if (!permissions?.canViewAppeals || tournaments.length === 0 || !user) {
      setPendingAppeals(0);
      return;
    }

    const hasGlobalAccess =
      role === 'admin' ||
      role === 'Manager' ||
      role === 'Head Judge' ||
      accountType === 'store';
    const userTournaments = user.tournamentAssignments || {};
    const headJudgeTeams = user.headJudgeTeamAssignments || {};

    // Only tournaments the appeals .read rule will actually allow.
    const readable = hasGlobalAccess
      ? tournaments
      : tournaments.filter(
          (t) => userTournaments[t.id] || headJudgeTeams[t.id],
        );

    if (readable.length === 0) {
      setPendingAppeals(0);
      return;
    }

    // Per-tournament counts summed into the badge; updated live.
    const counts = new Map<string, number>();
    const unsubscribes = readable.map((t) =>
      onValue(
        ref(db, `tournaments/${t.id}/appeals`),
        (snap) => {
          const data = snap.val() as Record<string, Appeal> | null;
          const open = data
            ? Object.values(data).filter(
                (a) => a.status === 'open' || a.status === 'in progress',
              ).length
            : 0;
          counts.set(t.id, open);
          setPendingAppeals(
            Array.from(counts.values()).reduce((sum, n) => sum + n, 0),
          );
        },
        () => {
          // Denied path or network blip — drop this tournament's
          // contribution rather than flicker the whole badge to zero.
          counts.set(t.id, 0);
        },
      ),
    );

    return () => unsubscribes.forEach((unsub) => unsub());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissions?.canViewAppeals, tournamentIdsKey, assignedKey, hjTeamKey, role, accountType]);

  // GRANULAR PENDING USERS: query only unapproved users
  useEffect(() => {
    if (!permissions?.canManage) {
      setPendingUsers(0);
      return;
    }

    const unapprovedQuery = query(
      ref(db, 'users'),
      orderByChild('approved'),
      equalTo(false)
    );

    const unsubscribe = onValue(unapprovedQuery, (snapshot) => {
      const data = snapshot.val();
      setPendingUsers(data ? Object.keys(data).length : 0);
    });

    return () => unsubscribe();
  }, [permissions?.canManage]);

  // Filter tournaments for deck-check access. HJ-team-role users
  // see all; otherwise only the tournaments they're personally
  // assigned to via deckCheckTeamAssignments or featureMatchTeam.
  const deckCheckTournaments = useMemo(() => {
    if (permissions?.hasDeckCheckRole) {
      return tournaments;
    }
    return tournaments.filter(t =>
      user?.deckCheckTeamAssignments?.[t.id] ||
      user?.featureMatchTeamAssignments?.[t.id]
    );
  }, [tournaments, permissions?.hasDeckCheckRole, user?.deckCheckTeamAssignments, user?.featureMatchTeamAssignments]);

  return {
    pendingAppeals,
    pendingPenalties: 0,
    pendingUsers,
    deckCheckTournaments,
  };
}

export default useNavBadgeCounts;
